import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../lib/firebase';

export interface RewardRule {
  id: string;
  museumId?: string;
  title: string;
  description: string;
  discountPercentage: number;
  targetType?: 'all' | 'category' | 'product';
  targetName?: string;
  conditionType?: string;
  conditionValue?: string;
  conditionLabel?: string;
  status?: string;
  active?: boolean;
}

export interface UserRewardDoc {
  id: string;
  userId: string;
  ruleId: string;
  ruleTitle?: string;
  discountPercentage?: number;
  targetType?: string;
  targetName?: string;
  museumId?: string;
  exhibitionId?: string;
  redeemed?: boolean;
  claimedAt?: any;
}

export interface ExperienceRewardResult {
  rewardRule: RewardRule | null;
  isClaimed: boolean;
  userRewardDoc: UserRewardDoc | null;
  loading: boolean;
}

export function useExperienceReward(experienceId: string | undefined, museumId: string | undefined): ExperienceRewardResult {
  const [rewardRule, setRewardRule] = useState<RewardRule | null>(null);
  const [isClaimed, setIsClaimed] = useState(false);
  const [userRewardDoc, setUserRewardDoc] = useState<UserRewardDoc | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!experienceId) {
      setRewardRule(null);
      setIsClaimed(false);
      setUserRewardDoc(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const rulesRef = collection(db, 'rewards_rules');
    const q = query(rulesRef);

    const unsubscribeRules = onSnapshot(q, async (snap) => {
      try {
        const eligibleDoc = snap.docs.find(d => {
          const data = d.data();
          const isActive = data.status === 'active' || data.status === undefined || data.active === true;
          if (!isActive) return false;

          const condType = data.conditionType || 'experience_complete';
          const condVal = (data.conditionValue || '').trim();

          // 1. Exact exhibitionId match
          if (condVal === experienceId || condVal === experienceId.trim()) return true;

          // 2. Wildcard or any_experience_complete
          if (condType === 'any_experience_complete' || condVal === 'any' || condVal === '') return true;

          // 3. Same museum match if rule belongs to this museum
          if (museumId && (!data.museumId || data.museumId === museumId)) return true;

          return false;
        });

        if (!eligibleDoc) {
          setRewardRule(null);
          setIsClaimed(false);
          setUserRewardDoc(null);
          setLoading(false);
          return;
        }

        const ruleData = { id: eligibleDoc.id, ...eligibleDoc.data() } as RewardRule;
        setRewardRule(ruleData);

        // Check if user has already claimed this reward
        const uid = auth.currentUser?.uid;
        if (uid && !auth.currentUser?.isAnonymous) {
          try {
            const claimRef = doc(db, 'user_rewards', `${uid}_${ruleData.id}`);
            const claimSnap = await getDoc(claimRef);
            if (claimSnap.exists()) {
              setIsClaimed(true);
              setUserRewardDoc({ id: claimSnap.id, ...claimSnap.data() } as UserRewardDoc);
            } else {
              setIsClaimed(false);
              setUserRewardDoc(null);
            }
          } catch {
            setIsClaimed(false);
            setUserRewardDoc(null);
          }
        } else {
          setIsClaimed(false);
          setUserRewardDoc(null);
        }
      } catch (err) {
        console.warn('[useExperienceReward] Error processing rules:', err);
      } finally {
        setLoading(false);
      }
    },
    (err) => {
      // Logged-out guests cannot read rewards_rules (rules: isAuth()).
      // Degrade gracefully instead of an uncaught snapshot error: rewards
      // simply don't resolve for guests (the guest incentive banner covers
      // sign-up). Members are unaffected — their listener keeps working.
      console.warn('[useExperienceReward] Rules snapshot unavailable:', err.code || err.message);
      setRewardRule(null);
      setIsClaimed(false);
      setUserRewardDoc(null);
      setLoading(false);
    });

    return () => unsubscribeRules();
  }, [experienceId, museumId, auth.currentUser?.uid]);

  return { rewardRule, isClaimed, userRewardDoc, loading };
}
